import type { DashboardSkillAncestriesQuery$data } from '../graphQLOperations/__generated__/DashboardSkillAncestriesQuery.graphql';

// `isTarget` is not in the generated type yet (pending the schema + `getSkillAncestries`
// query update). Augment the generated edge shape locally until the query selects
// `isTarget` and `pnpm run relay` regenerates the artifact — then this `& { isTarget }`
// becomes redundant and can be dropped.
type SkillAncestryEdge = NonNullable<DashboardSkillAncestriesQuery$data['skillAncestries']>[number] & {
  readonly isTarget: boolean;
};

type SkillAncestryEdges = readonly SkillAncestryEdge[] | undefined;

/** A skill within the level breakdown, annotated with graph-wide metadata. */
export interface SkillGraphNode {
  readonly id: number;
  readonly isTarget: boolean;
  /**
   * Global depth of this skill in the whole graph: the longest path from a root (a skill that
   * is not an ancestor of any other) down to it. Level 0 = the roots. This is a graph-wide
   * value and does **not** necessarily match the skill's index within its target breakdown.
   */
  readonly overallLevel: number;
  /**
   * Number of distinct targets that **transitively** depend on this skill (reach it through the
   * full prerequisite graph, never cutting at intermediate targets). A skill below a sub-target
   * therefore counts every target above it, not just its nearest one.
   */
  readonly targetSkillImpact: number;
}

/**
 * For every target skill, groups its prerequisite ancestors into depth-levels.
 *
 * Edges are `baseSkillId ⇐ ancestorSkillId` (the ancestor is a prerequisite of the base).
 * A skill is a **target** when it is the `baseSkillId` of an edge with `isTarget`.
 *
 * Returns `SkillGraphNode[][][]`: one entry per target. Each entry is that target's level
 * breakdown — `level 0 = [target]`, then deeper levels of prerequisites, with the skills
 * at each depth grouped together (so a fork yields several skills in one level). Other
 * targets reached along the way **cut** the descent (they get their own top-level entry).
 *
 * A skill reachable by several paths is placed at its **deepest** level (the longest path
 * from the target), so it always sits below everything that depends on it. The traversal
 * skips skills already on the current path, which keeps it terminating on cycles.
 *
 * Each skill appears **exactly once** across the whole result (no duplicates). When several
 * targets reach the same prerequisite, it is assigned to a single owner: the deepest
 * sub-target wins (a target that depends on another candidate yields ownership to it), and
 * between independent candidates the one with the longest path to the skill wins, then the
 * lowest id. Within its owner the skill is placed at its deepest local depth from that target.
 *
 * Each skill ID is replaced by a {@link SkillGraphNode} carrying graph-wide metadata:
 * `isTarget`, `overallLevel`, and `targetSkillImpact` (how many distinct targets transitively reach it).
 * Note `overallLevel` is a **global** depth (longest path from a graph root, level 0 = roots,
 * never cutting at targets) and is independent of the skill's index within the breakdown.
 */
export const getSkillsLevelByUnit = (skillAncestries: SkillAncestryEdges): SkillGraphNode[][][] => {
  if (!skillAncestries?.length) return [];

  const targets = new Set<number>();
  const ancestorsByBase = new Map<number, number[]>();

  skillAncestries.forEach(({ baseSkillId, ancestorSkillId, isTarget }) => {
    if (isTarget) targets.add(baseSkillId);
    ancestorsByBase.set(baseSkillId, [...(ancestorsByBase.get(baseSkillId) ?? []), ancestorSkillId]);
  });

  // For one target: the deepest depth at which it reaches each skill, cutting the descent at
  // other targets and skipping skills already on the current path (cycle guard).
  const reachFromTarget = (targetSkillId: number): Map<number, number> => {
    const depthBySkill = new Map<number, number>();
    const onPath = new Set<number>();

    const walk = (skillId: number, depth: number): void => {
      depthBySkill.set(skillId, Math.max(depthBySkill.get(skillId) ?? 0, depth));
      onPath.add(skillId);

      (ancestorsByBase.get(skillId) ?? []).forEach(ancestorSkillId => {
        if (targets.has(ancestorSkillId) || onPath.has(ancestorSkillId)) return;
        walk(ancestorSkillId, depth + 1);
      });

      onPath.delete(skillId);
    };

    walk(targetSkillId, 0);
    return depthBySkill;
  };

  // Every other target this target transitively depends on (reachable through prerequisites).
  // Used to give a shared skill to the deepest sub-target.
  const requiredTargetsOf = (targetSkillId: number): Set<number> => {
    const required = new Set<number>();
    const visited = new Set<number>();

    const explore = (skillId: number): void => {
      (ancestorsByBase.get(skillId) ?? []).forEach(ancestorSkillId => {
        if (visited.has(ancestorSkillId)) return;
        visited.add(ancestorSkillId);
        if (targets.has(ancestorSkillId)) required.add(ancestorSkillId);
        explore(ancestorSkillId);
      });
    };

    explore(targetSkillId);
    return required;
  };

  // Global depth of every skill: the longest path from a root (a skill that is not an ancestor
  // of any other) down to it, traversing the whole graph WITHOUT cutting at targets. Roots sit
  // at level 0; a cycle guard per path keeps it terminating.
  const ancestorSet = new Set<number>();
  ancestorsByBase.forEach(ancestors => ancestors.forEach(ancestorSkillId => ancestorSet.add(ancestorSkillId)));
  const roots = [...ancestorsByBase.keys()].filter(skillId => !ancestorSet.has(skillId));

  const globalLevelBySkill = new Map<number, number>();
  const walkGlobal = (skillId: number, depth: number, onPath: Set<number>): void => {
    globalLevelBySkill.set(skillId, Math.max(globalLevelBySkill.get(skillId) ?? 0, depth));
    onPath.add(skillId);
    (ancestorsByBase.get(skillId) ?? []).forEach(ancestorSkillId => {
      if (onPath.has(ancestorSkillId)) return;
      walkGlobal(ancestorSkillId, depth + 1, onPath);
    });
    onPath.delete(skillId);
  };
  roots.forEach(root => walkGlobal(root, 0, new Set<number>()));

  const orderedTargets = [...targets];
  const depthByTarget = new Map<number, Map<number, number>>();
  const requiredByTarget = new Map<number, Set<number>>();
  orderedTargets.forEach(target => {
    depthByTarget.set(target, reachFromTarget(target));
    requiredByTarget.set(target, requiredTargetsOf(target));
  });

  const depthFor = (target: number, skillId: number): number => depthByTarget.get(target)?.get(skillId) ?? 0;

  // Global impact: how many targets transitively reach each skill (full descent, no target cut),
  // so a skill below a sub-target counts every target above it, not just its nearest one.
  const reachAllFrom = (targetSkillId: number): Set<number> => {
    const reached = new Set<number>();
    const stack: number[] = [targetSkillId];
    while (stack.length) {
      const skillId = stack.pop() as number;
      if (reached.has(skillId)) continue;
      reached.add(skillId);
      (ancestorsByBase.get(skillId) ?? []).forEach(ancestorSkillId => {
        if (!reached.has(ancestorSkillId)) stack.push(ancestorSkillId);
      });
    }
    return reached;
  };

  const impactBySkill = new Map<number, number>();
  orderedTargets.forEach(target =>
    reachAllFrom(target).forEach(skillId => impactBySkill.set(skillId, (impactBySkill.get(skillId) ?? 0) + 1)),
  );

  // Assign every reached skill to a single owner target, with its graph-wide level.
  const allSkillIds = new Set<number>();
  depthByTarget.forEach(depths => depths.forEach((_depth, skillId) => allSkillIds.add(skillId)));

  const assignmentBySkill = new Map<number, { owner: number; level: number }>();
  allSkillIds.forEach(skillId => {
    const candidates = orderedTargets.filter(target => depthByTarget.get(target)?.has(skillId));

    // Deepest sub-target wins: drop any candidate that depends on another candidate.
    const deepestCandidates = candidates.filter(
      candidate => !candidates.some(other => other !== candidate && requiredByTarget.get(candidate)?.has(other)),
    );
    // Fall back to all candidates if a target dependency cycle leaves none.
    const owners = deepestCandidates.length ? deepestCandidates : candidates;

    // Between independent owners: longest path to the skill, then the lowest id.
    const owner = owners.reduce((best, candidate) => {
      if (depthFor(candidate, skillId) > depthFor(best, skillId)) return candidate;
      if (depthFor(candidate, skillId) === depthFor(best, skillId) && candidate < best) return candidate;
      return best;
    });

    // Placement level inside the breakdown: the deepest local depth this skill is reached at
    // across the targets (cut at targets). The exposed `overallLevel` is the global depth instead.
    const level = candidates.reduce((deepest, target) => Math.max(deepest, depthFor(target, skillId)), 0);

    assignmentBySkill.set(skillId, { owner, level });
  });

  const toNode = (skillId: number): SkillGraphNode => {
    const assignment = assignmentBySkill.get(skillId);
    return {
      id: skillId,
      isTarget: targets.has(skillId),
      // Global graph depth (longest path from a root). Falls back to the placement level for a
      // skill that no root reaches (e.g. a fully cyclic component without a root).
      overallLevel: globalLevelBySkill.get(skillId) ?? assignment?.level ?? 0,
      targetSkillImpact: impactBySkill.get(skillId) ?? 0,
    };
  };

  // Build each target's breakdown from the skills it owns, placed at their local placement level.
  // Empty levels (gaps left when a skill's level jumps past an unused depth) are dropped.
  return orderedTargets.map(target => {
    const ownedSkillIds = [...allSkillIds].filter(skillId => assignmentBySkill.get(skillId)?.owner === target);
    const deepestLevel = ownedSkillIds.reduce(
      (max, skillId) => Math.max(max, assignmentBySkill.get(skillId)?.level ?? 0),
      0,
    );
    const levels: number[][] = Array.from({ length: deepestLevel + 1 }, () => []);
    ownedSkillIds.forEach(skillId => levels[assignmentBySkill.get(skillId)?.level ?? 0].push(skillId));
    return levels.filter(level => level.length > 0).map(level => level.map(toNode));
  });
};
